/**
 * CustomerPortal.jsx
 *
 * Customer-facing portal for route tracking and approval-based route requests.
 *
 * GDPR / Security notes:
 *  - Route data is fetched via /routing/customer_routes — the backend resolves
 *    customer_id from the authenticated session (never from the request body).
 *  - No driver names, driver IDs, vehicle details, costings, or internal notes
 *    are ever sent to the customer from these endpoints.
 *  - Customers can submit/cancel their own pending requests but cannot create,
 *    edit, approve, or delete routes directly.
 *  - The delivery-details modal is opened with canEdit=false.
 *  - The completion modal shows delivery evidence (signature, photos, GPS) which
 *    is appropriate for the recipient company to view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isToday } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faClipboard } from '@fortawesome/free-regular-svg-icons';
import { faRoute, faDownload, faAnglesLeft, faAnglesRight, faLocationDot, faChartLine, faChartBar, faPlus } from '@fortawesome/free-solid-svg-icons';

import { useTheme } from './themeControl/ThemeProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { useAlert } from '../providers/AlertProvider';

import { ActivityIndicator } from './custom/ActivityIndicator';
import DeliveryDetailsModal from './custom/DeliveryDetailsModal';
import LocationMapModal from './LocationMapModal';
import CustomerRouteRequests from './custom/CustomerRouteRequests';

import { fetch_customer_routes, fetch_customer_routes_report, fetch_customer_tracking, fetch_customer_consignment_media, fetch_my_deliveries } from '../api/route';
import { getPortalKpis, downloadKpiPdf } from '../api/kpis';
import { checkCompletions, getCompletionByUuid, getCompletionImageUrl, generateDeliveryNote } from '../api/route_completions';

import { getDateRangeForQuery } from './lib/functions';

import './css/RouteStyles.css';
import './css/GlobalStyles.css';
import './css/LeftNavigation.css';
import './css/HomeStyles.css';
import { Row, Col } from 'react-bootstrap';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (date) => format(date, 'yyyy-MM-dd');

const downloadCSV = (rows) => {
    if (!rows || rows.length === 0) return;
    const headers = [
        'Date', 'Consignment', 'Route', 'Vehicle Reg',
        'Booked Time', 'Arrival Time', 'Departure Time',
        'Late', 'Late Reason', 'Delay (mins)', 'Delay Reason', 'Penalties',
    ];
    const esc = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const lines = [
        headers.join(','),
        ...rows.map((r) => {
            const dd = r.delivery_details || {};
            return [
                r.date ? r.date.split('-').reverse().join('/') : '',
                esc(r.route_ref),
                esc(r.route),
                r.vehicles?.reg || '',
                dd.booked_time || '',
                dd.arrival_time || '',
                dd.departure_time || '',
                dd.late ? 'Yes' : (dd.late === false ? 'No' : ''),
                esc(dd.late_reason),
                dd.delay_length != null ? dd.delay_length : '',
                esc(dd.delay_reason),
                dd.penalties != null ? esc(`£${Number(dd.penalties).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`) : '',
            ].join(',');
        })
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-routes-report.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const CustomerPortal = () => {
    const { theme } = useTheme();
    const { profile, isCustomer, isLoading: authLoading, logout, features } = useAuthProvider();

    const hasPalletTracking = !!(features?.palletforce || features?.pallettrack);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const isDark = theme === 'dark';

    // Guard: redirect non-customers away (belt-and-braces; server also enforces)
    useEffect(() => {
        if (!authLoading && profile && !isCustomer) {
            navigate('/');
        }
    }, [authLoading, profile, isCustomer, navigate]);

    // ── Calendar state ────────────────────────────────────────────────────────
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [routeData, setRouteData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // ── Completion state ──────────────────────────────────────────────────────
    const [completedRouteUuids, setCompletedRouteUuids] = useState({});

    // ── View toggle: calendar | report | tracking ──────────────────────────────
    const [view, setView] = useState('calendar');
    const [showLeftNav, setShowLeftNav] = useState(false);

    // ── Report state ──────────────────────────────────────────────────────────
    const [reportStartDate, setReportStartDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [myDeliveries, setMyDeliveries] = useState([]);
    const [selectedDeliveryId, setSelectedDeliveryId] = useState('');

    // ── Tracking state ────────────────────────────────────────────────────────
    const [trackingStartDate, setTrackingStartDate] = useState('');
    const [trackingEndDate, setTrackingEndDate] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [expandedManifests, setExpandedManifests] = useState(new Set());
    const [trackingConsignmentSearch, setTrackingConsignmentSearch] = useState('');
    const [mediaModal, setMediaModal] = useState(null); // { consignment_number, images, paperwork } | null
    const [mediaLoading, setMediaLoading] = useState(null); // consignment_number being loaded | null

    // ── KPI state ─────────────────────────────────────────────────────────────
    const [kpiPeriods, setKpiPeriods]           = useState(null);   // null = not yet loaded
    const [kpiLoading, setKpiLoading]           = useState(false);
    const [kpiSelected, setKpiSelected]         = useState(null);   // selected period object with rows
    const [kpiDownloading, setKpiDownloading]   = useState(false);

    // ── Delivery details modal ────────────────────────────────────────────────
    const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] = useState(false);
    const [deliveryDetailsRoute, setDeliveryDetailsRoute] = useState(null);
    const [deliveryDetailsRecord, setDeliveryDetailsRecord] = useState(null);
    const [deliveryDetailsLoading, setDeliveryDetailsLoading] = useState(false);

    // Set of route_uuids that have a late or delayed delivery record
    const [deliveryAlerts, setDeliveryAlerts] = useState(new Set());

    // ── Delivery notes (PDF download) modal ───────────────────────────────────
    const [showDeliveryNotesModal, setShowDeliveryNotesModal] = useState(false);
    const [selectedDeliveryNoteRoutes, setSelectedDeliveryNoteRoutes] = useState([]);
    const [isGeneratingDeliveryNotes, setIsGeneratingDeliveryNotes] = useState(false);

    // ── Completion viewer modal ───────────────────────────────────────────────
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [selectedCompletionData, setSelectedCompletionData] = useState(null);
    const [selectedCompletionRoute, setSelectedCompletionRoute] = useState(null);

    // ── Image viewer ──────────────────────────────────────────────────────────
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [selectedImageTitle, setSelectedImageTitle] = useState('');
    const [isImageLoading, setIsImageLoading] = useState(false);

    // ── Map modal ─────────────────────────────────────────────────────────────
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // ─────────────────────────────────────────────────────────────────────────
    // Data fetching
    // ─────────────────────────────────────────────────────────────────────────

    const fetchRoutes = useCallback(async () => {
        if (!currentDate || authLoading) return;
        setIsLoading(true);
        const range = getDateRangeForQuery(currentDate);
        try {
            const resp = await fetch_customer_routes(range);
            if (resp.status === 200 && Array.isArray(resp.data?.data)) {
                setRouteData(resp.data.data);
            } else if (resp.status === 403) {
                showAlert({ message: 'Your account is not linked to a customer. Please contact support.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
                setRouteData([]);
            } else {
                setRouteData([]);
            }
        } catch (err) {
            logger.error('CustomerPortal fetch error:', err);
            setRouteData([]);
        } finally {
            setTimeout(() => setIsLoading(false), 400);
        }
    }, [currentDate, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch this customer's delivery points once on mount for the report filter
    useEffect(() => {
        fetch_my_deliveries().then(res => {
            if (res.status === 200 && Array.isArray(res.data?.data)) {
                setMyDeliveries(res.data.data);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check completion status when a day is selected — same pattern as RouteCalendar
    useEffect(() => {
        if (!selectedDate || !routeData.length) return;
        const check = async () => {
            const dayRoutes = routeData.filter((r) => fmt(new Date(r.date)) === selectedDate);
            const dayUuids = dayRoutes.map((r) => r.route_uuid).filter(Boolean);
            if (dayUuids.length === 0) return;
            try {
                const [completionResult] = await Promise.all([
                    checkCompletions(dayUuids),
                ]);
                if (completionResult?.completed) {
                    setCompletedRouteUuids((prev) => ({ ...prev, ...completionResult.completed }));
                }
                // Build delivery alerts from embedded route data instead of separate API call
                const alertUuids = new Set(
                    dayRoutes
                        .filter(r => {
                            const dd = r.delivery_details;
                            return dd && (dd.late || (dd.delay_length !== null && dd.delay_length > 60));
                        })
                        .map(r => r.route_uuid)
                );
                setDeliveryAlerts(alertUuids);
            } catch (err) {
                logger.error('CustomerPortal completion check error:', err);
            }
        };
        check();
    }, [selectedDate, routeData]);

    // ─────────────────────────────────────────────────────────────────────────
    // Tracking
    // ─────────────────────────────────────────────────────────────────────────

    const handleFetchTracking = async () => {
        const hasConsignment = trackingConsignmentSearch.trim().length > 0;
        const hasDates = trackingStartDate && trackingEndDate;

        if (!hasConsignment && !hasDates) {
            showAlert({ message: 'Please select a date range or enter a consignment number.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            return;
        }
        if (hasDates && trackingStartDate > trackingEndDate) {
            showAlert({ message: 'Start date must be before end date.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            return;
        }
        setTrackingLoading(true);
        try {
            const resp = await fetch_customer_tracking(
                trackingStartDate || null,
                trackingEndDate || null,
                trackingConsignmentSearch.trim() || null,
            );
            if (resp.status === 200 && Array.isArray(resp.data)) {
                setTrackingData(resp.data);
                setExpandedManifests(new Set());
            } else if (resp.status === 403) {
                showAlert({ message: 'Your account is not linked to a customer. Please contact support.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
            } else if (resp.status === 404) {
                showAlert({ message: 'No Palletforce account number is configured for your account. Please contact support.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            } else {
                setTrackingData([]);
            }
        } catch (err) {
            logger.error('CustomerPortal tracking error:', err);
            setTrackingData([]);
        } finally {
            setTrackingLoading(false);
        }
    };

    const toggleManifestExpanded = (id) => {
        setExpandedManifests((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleLoadMedia = async (consignmentNumber) => {
        setMediaLoading(consignmentNumber);
        try {
            const resp = await fetch_customer_consignment_media(consignmentNumber);
            if (resp.status === 200 && resp.data) {
                setMediaModal({ consignment_number: consignmentNumber, images: resp.data.images || [], paperwork: resp.data.paperwork || [] });
            } else {
                showAlert({ message: resp.data?.error || 'Failed to load media.', type: 'danger', buttons: [{ type: 'primary', label: 'Close' }] });
            }
        } catch (e) {
            showAlert({ message: 'Failed to load media.', type: 'danger', buttons: [{ type: 'primary', label: 'Close' }] });
        } finally {
            setMediaLoading(null);
        }
    };

    const renderMediaModal = () => {
        if (!mediaModal) return null;
        const { consignment_number, images, paperwork } = mediaModal;
        const sigImages = images.filter(img => img.image_type === 'signature');
        const delImages = images.filter(img => img.image_type !== 'signature');
        const PW_TYPE = { D: 'Delivery Depot', R: 'Requesting Depot' };
        const overlayStyle = {
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
        };
        const panelStyle = {
            background: isDark ? '#1e1e1e' : '#fff',
            color: isDark ? '#ddd' : '#222',
            borderRadius: 12, width: '100%', maxWidth: 820,
            maxHeight: '88vh', overflowY: 'auto',
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
            padding: 28,
        };
        const sectionTitle = (t) => (
            <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 12, marginTop: 20, borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}`, paddingBottom: 6 }}>{t}</div>
        );
        const imgGrid = (imgs) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {imgs.map((img, i) => (
                    img.signed_url ? (
                        <a key={i} href={img.signed_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                            <img
                                src={img.signed_url}
                                alt={img.image_type || 'Delivery image'}
                                style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 6, border: `1px solid ${isDark ? '#444' : '#ccc'}`, cursor: 'zoom-in' }}
                            />
                        </a>
                    ) : (
                        <div key={i} style={{ width: 160, height: 120, borderRadius: 6, background: isDark ? '#333' : '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em', opacity: 0.5 }}>
                            Unavailable
                        </div>
                    )
                ))}
            </div>
        );
        return (
            <div style={overlayStyle} onClick={() => setMediaModal(null)}>
                <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '1.1em' }}>Images &amp; Paperwork</div>
                            <div style={{ opacity: 0.6, fontSize: '0.88em', marginTop: 2 }}>Consignment {consignment_number}</div>
                        </div>
                        <button onClick={() => setMediaModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.4em', cursor: 'pointer', opacity: 0.6, color: 'inherit', lineHeight: 1 }}>✕</button>
                    </div>

                    {sectionTitle('Signatures')}
                    {sigImages.length > 0 ? imgGrid(sigImages) : <div style={{ opacity: 0.5, fontSize: '0.9em' }}>No signature images stored.</div>}

                    {sectionTitle('Delivery Images')}
                    {delImages.length > 0 ? imgGrid(delImages) : <div style={{ opacity: 0.5, fontSize: '0.9em' }}>No delivery images stored.</div>}

                    {sectionTitle('Paperwork')}
                    {paperwork.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {paperwork.map((pw, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: isDark ? '#2a2a2a' : '#f5f5f5' }}>
                                    <span style={{ fontSize: '1.4em' }}>📄</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600 }}>{PW_TYPE[pw.paperwork_type] || pw.paperwork_type}</div>
                                        {pw.downloaded_at && <div style={{ opacity: 0.55, fontSize: '0.82em' }}>{pw.downloaded_at.slice(0, 10)}</div>}
                                    </div>
                                    {pw.signed_url && (
                                        <a href={pw.signed_url} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 14px', borderRadius: 6, background: '#007bff', color: '#fff', textDecoration: 'none', fontSize: '0.88em', fontWeight: 600 }}>
                                            View PDF
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : <div style={{ opacity: 0.5, fontSize: '0.9em' }}>No paperwork stored.</div>}
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Report
    // ─────────────────────────────────────────────────────────────────────────

    const handleFetchReport = async () => {
        if (!reportStartDate || !reportEndDate) {
            showAlert({ message: 'Please select a start date and end date.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            return;
        }
        if (reportStartDate > reportEndDate) {
            showAlert({ message: 'Start date must be before end date.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            return;
        }
        setReportLoading(true);
        try {
            const resp = await fetch_customer_routes_report(reportStartDate, reportEndDate, selectedDeliveryId || null);
            if (resp.status === 200 && Array.isArray(resp.data?.data)) {
                const rows = resp.data.data;
                setReportData(rows);
                // Check real completion status from route_completions
                const uuids = rows.map((r) => r.route_uuid).filter(Boolean);
                if (uuids.length > 0) {
                    const completionResult = await checkCompletions(uuids);
                    if (completionResult?.completed) {
                        setCompletedRouteUuids((prev) => ({ ...prev, ...completionResult.completed }));
                    }
                }
            } else if (resp.status === 403) {
                showAlert({ message: 'Your account is not linked to a customer.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
            } else {
                setReportData([]);
            }
        } catch (err) {
            logger.error('CustomerPortal report error:', err);
            setReportData([]);
        } finally {
            setReportLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Delivery Details modal
    // ─────────────────────────────────────────────────────────────────────────

    const handleOpenDeliveryDetails = async (route) => {
        setDeliveryDetailsRoute(route);
        setDeliveryDetailsRecord(null);
        setDeliveryDetailsLoading(true);
        setShowDeliveryDetailsModal(true);
        // Use embedded delivery_details from the route data (already customer-scoped)
        if (route.delivery_details) {
            setDeliveryDetailsRecord(route.delivery_details);
        }
        setDeliveryDetailsLoading(false);
    };

    const handleCloseDeliveryDetails = () => {
        setShowDeliveryDetailsModal(false);
        setDeliveryDetailsRoute(null);
        setDeliveryDetailsRecord(null);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Completion viewer
    // ─────────────────────────────────────────────────────────────────────────

    const handleViewCompletion = async (route_uuid, routeInfo) => {
        try {
            const result = await getCompletionByUuid(route_uuid);
            if (result?.success && result?.completion) {
                setSelectedCompletionData(result.completion);
                setSelectedCompletionRoute(routeInfo);
                setShowCompletionModal(true);
            } else {
                showAlert({ message: result?.error || 'Could not load completion details.', type: 'warning', buttons: [{ type: 'primary', label: 'Close' }] });
            }
        } catch (err) {
            logger.error('Completion fetch error:', err);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Image viewer
    // ─────────────────────────────────────────────────────────────────────────

    const handleImageClick = async (url, title) => {
        setSelectedImageTitle(title);
        setIsImageLoading(true);
        setShowImageModal(true);
        const result = await getCompletionImageUrl(url);
        setIsImageLoading(false);
        if (result.success && result.image_url) {
            setSelectedImageUrl(result.image_url);
        } else {
            setShowImageModal(false);
            showAlert({ message: 'Failed to load image.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
        }
    };

    const handleDownloadAll = async (imagePaths, label) => {
        for (let i = 0; i < imagePaths.length; i++) {
            const result = await getCompletionImageUrl(imagePaths[i]);
            if (result.success && result.image_url) {
                try {
                    const response = await fetch(result.image_url);
                    const blob = await response.blob();
                    const ext = blob.type.split('/')[1] || 'jpg';
                    const objectUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = objectUrl;
                    a.download = `${label}_${i + 1}.${ext}`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(objectUrl);
                    await new Promise((r) => setTimeout(r, 300));
                } catch (err) {
                    logger.error('Image download error:', err);
                }
            }
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Delivery Notes (PDF generation)
    // ─────────────────────────────────────────────────────────────────────────

    const currentDayRoutes = selectedDate
        ? routeData.filter((r) => fmt(new Date(r.date)) === selectedDate)
        : [];

    const completedRoutesForDay = currentDayRoutes.filter((r) => completedRouteUuids[r.route_uuid] === true);

    const handleGenerateDeliveryNotes = async () => {
        const selected = completedRoutesForDay.filter((r) => selectedDeliveryNoteRoutes.includes(r.route_uuid));
        if (selected.length === 0) return;
        setIsGeneratingDeliveryNotes(true);
        let ok = 0;
        let fail = 0;
        for (let i = 0; i < selected.length; i++) {
            try {
                const compResult = await getCompletionByUuid(selected[i].route_uuid);
                if (!compResult?.success || !compResult?.completion) { fail++; continue; }
                const { status } = await generateDeliveryNote(selected[i], compResult.completion);
                if (status === 200) ok++; else fail++;
                if (i < selected.length - 1) await new Promise((r) => setTimeout(r, 500));
            } catch { fail++; }
        }
        setIsGeneratingDeliveryNotes(false);
        setShowDeliveryNotesModal(false);
        setSelectedDeliveryNoteRoutes([]);
        showAlert({
            message: fail === 0
                ? `${ok} delivery note${ok !== 1 ? 's' : ''} downloaded successfully.`
                : `${ok} downloaded, ${fail} failed.`,
            type: fail === 0 ? 'success' : 'warning',
            buttons: [{ type: 'primary', label: 'Close' }],
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Calendar rendering
    // ─────────────────────────────────────────────────────────────────────────

    const generateCalendar = () => {
        const startDate = startOfWeek(startOfMonth(currentDate));
        const endDate = endOfWeek(endOfMonth(currentDate));
        const days = [];
        let day = startDate;

        while (day <= endDate) {
            const formattedDate = fmt(day);
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const dayRoutes = routeData.filter((r) => fmt(new Date(r.date)) === formattedDate);

            days.push(
                <motion.div
                    key={formattedDate}
                    className={`day ${isCurrentMonth ? 'current-month' : 'other-month'} ${isToday(day) ? 'today' : ''}`}
                    onClick={() => setSelectedDate(formattedDate)}
                    whileHover={{ scale: 1.05 }}
                    style={{ height: '12.5vh', zIndex: 0 }}
                >
                    <div className="date">{format(day, 'd')}</div>
                    <div className="event-container" style={{ height: '100%', overflowY: 'auto', padding: 5, paddingBottom: 25, borderRadius: 5 }}>
                        {dayRoutes.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <FontAwesomeIcon style={{ fontSize: '2.5rem', width: 40, cursor: 'crosshair' }} icon={faRoute} />
                            </div>
                        )}
                    </div>
                </motion.div>
            );
            day = addDays(day, 1);
        }
        return days;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Day view rendering
    // ─────────────────────────────────────────────────────────────────────────

    const renderDayView = () => {
        if (!selectedDate) return null;

        return (
            <motion.div
                className="modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
            >
                <div
                    className="modal-content"
                    style={{
                        border: isDark ? '5px solid #fff' : '5px solid #000',
                        backgroundColor: isDark ? '#080808' : '#fff',
                        width: '100%',
                    }}
                >
                    {/* Top bar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '0px 20px' }}>
                        <span style={{ fontSize: 22, color: isDark ? '#fff' : '#000' }}>
                            {format(new Date(selectedDate), 'EEE, MMMM dd')}
                        </span>

                        {/* Delivery Notes button — only shown when completed routes exist */}
                        {completedRoutesForDay.length > 0 && (
                            <span
                                className="add-event-button"
                                onClick={() => { setShowDeliveryNotesModal(true); setSelectedDeliveryNoteRoutes([]); }}
                            >
                                Delivery Notes
                            </span>
                        )}

                        <button className="btn btn-secondary" style={{ marginTop: '0px', width: 100 }} onClick={() => setSelectedDate(null)}>
                            Close
                        </button>
                    </div>

                    {/* Routes table */}
                    {currentDayRoutes.length > 0 ? (
                        <div style={{ overflowX: 'auto', padding: '0 10px 10px' }}>
                            <table className="route-table" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', width: 40 }}>POD</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', width: 40 }}>Delivery Info</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', width: 130 }}>Consignment</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px' }}>Route</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px' }}>Comments</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', width: 120 }}>Vehicle Reg</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', width: 100 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDayRoutes.map((route, index) => {
                                        const isCompleted = completedRouteUuids[route.route_uuid] === true;
                                        const rowBg = isDark ? (index % 2 === 0 ? '#3a3a3a' : '#2e2e2e') : (index % 2 === 0 ? '#f9f9f9' : '#eaeaea');

                                        return (
                                            <tr key={route.route_uuid || index} style={{ background: rowBg, color: isDark ? '#ddd' : '#333' }}>
                                                {/* Eye — grey unless completed */}
                                                <td style={{ padding: '8px 12px', textAlign: 'center', width: 40 }}>
                                                    <FontAwesomeIcon
                                                        icon={faEye}
                                                        style={{
                                                            fontSize: '1.2rem',
                                                            width: 18,
                                                            color: isCompleted ? 'rgba(34,197,94,1)' : 'rgba(150,150,150,0.5)',
                                                            cursor: isCompleted ? 'pointer' : 'default',
                                                        }}
                                                        title={isCompleted ? 'View completion details' : 'Not yet completed'}
                                                        onClick={() => isCompleted && handleViewCompletion(route.route_uuid, route)}
                                                    />
                                                </td>

                                                {/* Clipboard (delivery details) — red if late/delayed */}
                                                <td style={{ padding: '8px 12px', textAlign: 'center', width: 40 }}>
                                                    {route.route_ref ? (
                                                        <FontAwesomeIcon
                                                            icon={faClipboard}
                                                            style={{
                                                                fontSize: '1.2rem',
                                                                width: 18,
                                                                cursor: 'pointer',
                                                                color: deliveryAlerts.has(route.route_uuid)
                                                                    ? 'rgba(220, 53, 69, 0.95)'
                                                                    : isDark ? '#ccc' : '#555'
                                                            }}
                                                            title={deliveryAlerts.has(route.route_uuid) ? 'Late or delayed delivery' : 'View delivery details'}
                                                            onClick={() => handleOpenDeliveryDetails(route)}
                                                        />
                                                    ) : (
                                                        <span style={{ color: 'rgba(150,150,150,0.4)' }}>—</span>
                                                    )}
                                                </td>

                                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{route.route_ref || '—'}</td>
                                                <td style={{ padding: '8px 12px' }}>{route.route || '—'}</td>
                                                <td style={{ padding: '8px 12px' }}>{route.job_comments || '—'}</td>
                                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{route.vehicles?.reg || '—'}</td>

                                                {/* Status badge */}
                                                <td style={{ padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        padding: '3px 10px',
                                                        borderRadius: 12,
                                                        fontSize: '0.8em',
                                                        fontWeight: 600,
                                                        background: isCompleted ? 'rgba(34,197,94,0.2)' : route.confirmed ? 'rgba(26,221,247,0.2)' : 'rgba(200,200,200,0.2)',
                                                        color: isCompleted ? 'rgba(34,197,94,1)' : route.confirmed ? 'rgba(26,157,215,1)' : (isDark ? '#aaa' : '#666'),
                                                        border: `1px solid ${isCompleted ? 'rgba(34,197,94,0.4)' : route.confirmed ? 'rgba(26,221,247,0.4)' : 'rgba(200,200,200,0.4)'}`,
                                                    }}>
                                                        {isCompleted ? 'Completed' : route.confirmed ? 'Confirmed' : 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <h5 style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>No routes for this date</h5>
                    )}
                </div>
            </motion.div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Tracking view rendering
    // ─────────────────────────────────────────────────────────────────────────

    // Full Palletforce event code map — label + hover description
    const EVENT_CODES = {
        'ABRT':   { label: 'Delivery Aborted',             desc: 'The delivery attempt was aborted before completion.' },
        'AGRED':  { label: 'Agreed Out of Hours',          desc: 'Agreed out-of-hours delivery arranged with the consignee.' },
        'ARDL':   { label: 'Arrived Delivery Point',       desc: 'Vehicle has arrived at the delivery location.' },
        'ARRD':   { label: 'Arrived Delivery Depot',       desc: 'Consignment has arrived at the delivering depot.' },
        'ARRH':   { label: 'Arrived at Hub',               desc: 'Consignment has arrived at the Palletforce hub.' },
        'BKLPOC': { label: 'POC Received (Backload)',      desc: 'Proof of collection received via backload.' },
        'BKLPOD': { label: 'POD Received (Backload)',      desc: 'Proof of delivery received via backload.' },
        'CODD':   { label: 'Change of Delivery Date',      desc: 'The scheduled delivery date has been changed.' },
        'COUR':   { label: 'Courier Delivery',             desc: 'Consignment is being delivered by courier.' },
        'DAM':    { label: 'Received Damaged',             desc: 'Depot received the consignment in a damaged state.' },
        'DELV':   { label: 'Loaded for Delivery',          desc: 'Scanned onto the delivery vehicle.' },
        'DEPH':   { label: 'Left Hub for Depot',           desc: 'Consignment has departed the hub towards the delivery depot.' },
        'DMGD':   { label: 'Delivered Damaged',            desc: 'Consignment was delivered but damage was noted.' },
        'DONR':   { label: 'En Route',                     desc: 'Consignment is en route to the delivery location.' },
        'FAILC':  { label: 'Failed – Consignee',           desc: 'Delivery failed due to the consignee (e.g. refused, closed).' },
        'FAILD':  { label: 'Failed – Delivery Depot',      desc: 'Delivery failed due to an issue at the delivery depot.' },
        'FAILR':  { label: 'Failed – Requesting Depot',    desc: 'Delivery failed due to an issue at the requesting depot.' },
        'GEN':    { label: 'Depot Scan',                   desc: 'General depot scan (legacy event code).' },
        'HINH':   { label: 'Held In Hub',                  desc: 'Consignment is being held at the hub.' },
        'HLTIN':  { label: 'Late Held Freight',            desc: 'Freight arrived late and is being held.' },
        'HUBD':   { label: 'Hub Damaged',                  desc: 'Consignment was damaged at the hub.' },
        'NCCO':   { label: 'Not Collected – Cancelled',    desc: 'Collection not made – order was cancelled.' },
        'NCCR':   { label: 'Not Collected – Closed',       desc: 'Collection not made – premises closed or nobody on site.' },
        'NCD':    { label: 'Not Collected – Duplicate',    desc: 'Collection not made – already collected or duplicate.' },
        'NCNR':   { label: 'Not Collected – Not Ready',    desc: 'Collection not made – goods not ready or loading delays.' },
        'NTC':    { label: 'Nothing to Collect',           desc: 'No freight available for collection at this point.' },
        'PCC':    { label: 'Pending Customs',              desc: 'Consignment is pending customs clearance.' },
        'PFF':    { label: 'Palletforce Failed',           desc: 'Delivery has failed within the Palletforce network.' },
        'POC':    { label: 'Collected',                    desc: 'Proof of collection – consignment has been collected.' },
        'POD':    { label: 'Delivered ✓',                  desc: 'Proof of delivery received – consignment successfully delivered.' },
        'PODD':   { label: 'Delivered Damaged',            desc: 'POD received but consignment was noted as damaged on delivery.' },
        'PODE':   { label: 'Delivered with Exception',     desc: 'POD received but with a noted exception (e.g. short delivery).' },
        'PREF':   { label: 'Refused at Delivery',          desc: 'Consignment was refused at the delivery point.' },
        'RECD':   { label: 'Received Damaged',             desc: 'Consignment received at depot in a damaged state.' },
        'REDC':   { label: 'Re-delivery – Consignee',      desc: 'Re-delivery required due to the consignee.' },
        'REDD':   { label: 'Re-delivery – Depot',          desc: 'Re-delivery required due to the delivery depot.' },
        'REDL':   { label: 'Re-delivery',                  desc: 'A re-delivery attempt has been scheduled.' },
        'REDR':   { label: 'Re-delivery – Req. Depot',     desc: 'Re-delivery required due to the requesting depot.' },
        'REF':    { label: 'Refused',                      desc: 'Consignment refused at the delivery point.' },
        'RETS':   { label: 'Return to Sender',             desc: 'Pallets are being returned to the sender.' },
        'SCDO':   { label: 'On Day 1 Trunk',               desc: 'Scanned onto the Day 1 sort trunk.' },
        'SCDV':   { label: 'On Delivery Vehicle',          desc: 'Scanned onto the delivery vehicle.' },
        'SCOT':   { label: 'On Trunk',                     desc: 'Scanned onto a trunk vehicle in transit.' },
        'THS':    { label: 'Transited Hub',                desc: 'Consignment has transited through the hub.' },
        'TID':    { label: 'Taken In Day',                 desc: 'Consignment taken in during the day.' },
        'WAIT':   { label: 'Waiting Time',                 desc: 'Waiting time incurred at the delivery point.' },
        'WAITC':  { label: 'Waiting – Consignee',          desc: 'Waiting time due to the consignee.' },
        'WAITD':  { label: 'Waiting – Depot',              desc: 'Waiting time due to the delivery depot.' },
    };
    const eventCodeLabel = (code) => EVENT_CODES[code]?.label || code;
    const eventCodeDesc  = (code) => EVENT_CODES[code]?.desc  || code;
    const eventCodeColor = (code) => {
        if (['POD', 'BKLPOD', 'POC', 'BKLPOC'].includes(code)) return 'rgba(34,197,94,1)';
        if (['PFF', 'FAILC', 'FAILD', 'FAILR', 'ABRT', 'PREF', 'REF', 'REFD', 'DMGD', 'PODD', 'HUBD', 'DAM', 'RECD'].includes(code)) return 'rgba(220,53,69,0.9)';
        if (['REDC', 'REDD', 'REDL', 'REDR', 'PODE', 'WAIT', 'WAITC', 'WAITD', 'NCCO', 'NCCR', 'NCD', 'NCNR', 'HLTIN'].includes(code)) return 'rgba(255,165,0,0.9)';
        if (['ARDL', 'DONR', 'SCDV', 'DELV', 'SCOT', 'DEPH', 'THS', 'ARRD'].includes(code)) return 'rgba(26,157,215,1)';
        return isDark ? '#bbb' : '#555';
    };

    // Determine on-time status from tracking events + booking window
    const getOnTimeStatus = (events, bookingFrom, bookingTo) => {
        if (!bookingFrom || !bookingTo) return null;
        const podEvent = [...events].reverse().find(e => ['POD', 'BKLPOD'].includes(e.event_code));
        if (!podEvent || !podEvent.event_datetime) return null;
        // Compare time-of-day portion only (booking times are HH:MM strings)
        const podTime = new Date(podEvent.event_datetime).toTimeString().slice(0, 5); // "HH:MM"
        const onTime = podTime >= bookingFrom.slice(0, 5) && podTime <= bookingTo.slice(0, 5);
        return { onTime, podTime, podDatetime: podEvent.event_datetime };
    };

    // ─────────────────────────────────────────────────────────────────────────
    // KPI view (customer portal — read-only saved KPIs)
    // ─────────────────────────────────────────────────────────────────────────

    const handleLoadKpis = async () => {
        setKpiLoading(true);
        setKpiPeriods(null);
        setKpiSelected(null);
        try {
            const r = await getPortalKpis();
            if (r.ok && Array.isArray(r.data)) {
                setKpiPeriods(r.data);
                if (r.data.length === 1) setKpiSelected(r.data[0]);
            } else {
                setKpiPeriods([]);
            }
        } catch {
            setKpiPeriods([]);
        } finally {
            setKpiLoading(false);
        }
    };

    const KPI_STATUS_CFG = {
        on_time: { label: 'On Time',  color: 'rgba(34,197,94,1)' },
        late:    { label: 'Late',     color: 'rgba(220,53,69,0.9)' },
        no_pod:  { label: 'No POD',   color: 'rgba(253,126,20,0.9)' },
        return:  { label: 'Return',   color: 'rgba(108,117,125,0.9)' },
        exempt:  { label: 'Exempt',   color: 'rgba(23,162,184,0.9)' },
    };

    const renderKpis = () => {
        const bd = `1px solid ${isDark ? '#444' : '#e0e0e0'}`;
        const fmtD = (v) => v ? v.slice(0, 10).split('-').reverse().join('/') : '—';
        const rows = kpiSelected?.rows || [];
        const counted = rows.filter(r => !r.npc);
        const npcCount = rows.length - counted.length;
        const counts = {};
        counted.forEach(r => { counts[r.kpi_status] = (counts[r.kpi_status] || 0) + 1; });
        const pass = (counts.on_time || 0) + (counts.return || 0) + (counts.exempt || 0);
        const total = counted.length;
        const pct = total > 0 ? Math.round((pass / total) * 100) : null;

        return (
            <div style={{ padding: 20 }}>
                {/* Controls */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 24, padding: 20, borderRadius: 10, background: isDark ? '#1a1a1a' : '#f5f5f5', border: `1px solid ${isDark ? '#333' : '#ddd'}` }}>
                    <button
                        onClick={handleLoadKpis}
                        disabled={kpiLoading}
                        style={{ padding: '9px 22px', border: 'none', borderRadius: 6, background: kpiLoading ? '#ccc' : '#007bff', color: '#fff', cursor: kpiLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.95em' }}
                    >
                        {kpiLoading ? 'Loading…' : kpiPeriods === null ? 'Load KPIs' : 'Refresh'}
                    </button>

                    {kpiPeriods && kpiPeriods.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>Period</label>
                            <select
                                value={kpiSelected?.period || ''}
                                onChange={e => {
                                    const found = kpiPeriods.find(p => p.period === e.target.value);
                                    setKpiSelected(found || null);
                                }}
                                style={{ padding: '8px 12px', borderRadius: 6, border: `1px solid ${isDark ? '#555' : '#ccc'}`, backgroundColor: isDark ? '#2a2a2a' : '#fff', color: isDark ? '#eee' : '#222', fontSize: '0.95em' }}
                            >
                                {kpiPeriods.map(p => {
                                    const [s, e2] = (p.period || '').split('_');
                                    const label = s && e2 ? `${s.replace(/-/g, '/')} – ${e2.replace(/-/g, '/')}` : p.period;
                                    return <option key={p.period} value={p.period}>{label}</option>;
                                })}
                            </select>
                        </div>
                    )}

                    {kpiSelected && (
                        <button
                            onClick={async () => {
                                setKpiDownloading(true);
                                await downloadKpiPdf(kpiSelected.period);
                                setKpiDownloading(false);
                            }}
                            disabled={kpiDownloading}
                            style={{ padding: '9px 18px', border: 'none', borderRadius: 6, background: kpiDownloading ? '#ccc' : '#dc3545', color: '#fff', cursor: kpiDownloading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.95em', alignSelf: 'flex-end' }}
                        >
                            {kpiDownloading ? 'Generating…' : 'Download PDF'}
                        </button>
                    )}
                </div>

                {/* Not loaded yet */}
                {kpiPeriods === null && !kpiLoading && (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>Click "Load KPIs" to view your performance reports.</p>
                )}

                {/* No data */}
                {kpiPeriods !== null && kpiPeriods.length === 0 && (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>No KPI reports have been published for your account yet.</p>
                )}

                {/* Summary + table */}
                {kpiSelected && rows.length > 0 && (
                    <>
                        {/* Score bar */}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18, padding: '12px 18px', background: isDark ? '#1a1a1a' : '#f5f5f5', borderRadius: 8, border: `1px solid ${isDark ? '#333' : '#ddd'}` }}>
                            <div style={{ fontWeight: 800, fontSize: '1.6em', color: pct >= 95 ? 'rgba(34,197,94,1)' : pct >= 80 ? 'rgba(253,126,20,0.9)' : 'rgba(220,53,69,0.9)' }}>{pct}%</div>
                            <div style={{ opacity: 0.6, fontSize: '0.9em' }}>KPI Score</div>
                            <div style={{ borderLeft: `1px solid ${isDark ? '#444' : '#ccc'}`, height: 28 }} />
                            {Object.entries(KPI_STATUS_CFG).map(([key, cfg]) =>
                                counts[key] ? (
                                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <span style={{ background: cfg.color, color: '#fff', borderRadius: 3, padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>{cfg.label}</span>
                                        <span style={{ fontWeight: 700 }}>{counts[key]}</span>
                                    </div>
                                ) : null
                            )}
                            {npcCount > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <span style={{ background: '#8b5cf6', color: '#fff', borderRadius: 3, padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>NPC</span>
                                    <span style={{ fontWeight: 700 }}>{npcCount}</span>
                                </div>
                            )}
                            <div style={{ opacity: 0.5, fontSize: '0.88em', marginLeft: 'auto' }}>{total} counted{npcCount > 0 ? ` · ${rows.length} total` : ''}</div>
                        </div>

                        {/* Table */}
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92em' }}>
                                <thead>
                                    <tr style={{ background: isDark ? '#333' : '#e0e0e0', color: isDark ? '#fff' : '#333' }}>
                                        {['Status', 'Consignment', 'Collection', 'Delivery Date', 'POD Date', 'POD Time', 'Deliver To', 'Postcode', 'Pallets', 'Your Ref', 'Comments'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', borderBottom: `2px solid ${isDark ? '#555' : '#bbb'}`, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => {
                                        const cfg = KPI_STATUS_CFG[row.kpi_status] || { label: row.kpi_status, color: '#999' };
                                        const rowBg = isDark ? (i % 2 === 0 ? '#2a2a2a' : '#222') : (i % 2 === 0 ? '#f9f9f9' : '#fff');
                                        const npcBg = isDark ? '#1e1b2e' : '#f3f0ff';
                                        return (
                                            <tr key={row.id || i} style={{ background: row.npc ? npcBg : rowBg, opacity: row.npc ? 0.75 : 1, color: isDark ? '#ddd' : '#333' }}>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>
                                                    {row.npc
                                                        ? <span style={{ background: '#8b5cf6', color: '#fff', borderRadius: 3, padding: '2px 6px', fontSize: '10px', fontWeight: 700 }}>NPC</span>
                                                        : <span style={{ background: cfg.color, color: '#fff', borderRadius: 3, padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>{cfg.label}</span>
                                                    }
                                                </td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.consignment_number || '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{fmtD(row.collection_date)}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{fmtD(row.delivery_date)}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap', fontWeight: row.kpi_status === 'late' ? 700 : 400, color: row.kpi_status === 'late' ? 'rgba(220,53,69,0.9)' : 'inherit' }}>{fmtD(row.pod_date)}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{row.pod_time || '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd }}>{row.delivery_name || '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{row.delivery_postcode || '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, textAlign: 'center' }}>{row.number_of_pallets ?? '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, opacity: 0.7 }}>{row.customer_reference || '—'}</td>
                                                <td style={{ padding: '8px 12px', borderBottom: bd, opacity: 0.8, fontStyle: row.admin_comment ? 'normal' : 'italic' }}>{row.admin_comment || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderTracking = () => {
        const inputStyle = {
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#eee' : '#222',
            fontSize: '0.95em',
        };
        const bd = `1px solid ${isDark ? '#444' : '#e0e0e0'}`;
        const consignmentFilter = trackingConsignmentSearch.trim().toLowerCase();
        const filteredData = trackingData
            ? (consignmentFilter
                ? trackingData.filter(m => (m.consignment_number || '').toLowerCase().includes(consignmentFilter))
                : trackingData)
            : [];

        return (
            <div style={{ padding: 20 }}>
                {/* Date range picker */}
                <div style={{
                    display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
                    padding: 20, borderRadius: 10,
                    background: isDark ? '#1a1a1a' : '#f5f5f5',
                    border: `1px solid ${isDark ? '#333' : '#ddd'}`,
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>From</label>
                        <input type="date" value={trackingStartDate} onChange={(e) => setTrackingStartDate(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>To</label>
                        <input type="date" value={trackingEndDate} onChange={(e) => setTrackingEndDate(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>Consignment No.</label>
                        <input
                            type="text"
                            placeholder="e.g. 123456789"
                            value={trackingConsignmentSearch}
                            onChange={(e) => setTrackingConsignmentSearch(e.target.value)}
                            style={{ ...inputStyle, width: 180 }}
                        />
                    </div>
                    <button
                        onClick={handleFetchTracking}
                        disabled={trackingLoading}
                        style={{
                            padding: '9px 22px', border: 'none', borderRadius: 6,
                            background: trackingLoading ? '#ccc' : '#007bff',
                            color: '#fff', cursor: trackingLoading ? 'not-allowed' : 'pointer',
                            fontWeight: 600, fontSize: '0.95em',
                        }}
                    >
                        {trackingLoading ? 'Loading…' : 'Get Tracking'}
                    </button>
                </div>

                {/* Results */}
                {trackingData === null ? (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>Select a date range and click "Get Tracking".</p>
                ) : trackingData.length === 0 ? (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>No consignments found for this period.</p>
                ) : (
                    <>
                        <p style={{ opacity: 0.7, marginBottom: 12, fontSize: '0.9em' }}>
                            {filteredData.length}{consignmentFilter && filteredData.length !== trackingData.length ? ` of ${trackingData.length}` : ''} consignment{filteredData.length !== 1 ? 's' : ''} found
                            {consignmentFilter && filteredData.length === 0 && <span style={{ marginLeft: 8, color: 'rgba(220,53,69,0.85)' }}>— no match for "{trackingConsignmentSearch.trim()}"</span>}
                        </p>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92em' }}>
                                <thead>
                                    <tr style={{ background: isDark ? '#333' : '#e0e0e0', color: isDark ? '#fff' : '#333' }}>
                                        {['', 'Collection', 'Delivery', 'Consignment No.', 'Tracking No.', 'Deliver To', 'Postcode', 'Pallets', 'Weight', 'Your Ref', 'Booking Window', 'On Time', 'Latest Event'].map((h) => (
                                            <th key={h} style={{ padding: '10px 12px', borderBottom: `2px solid ${isDark ? '#555' : '#bbb'}`, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((m, i) => {
                                        const rowBg = isDark ? (i % 2 === 0 ? '#2a2a2a' : '#222') : (i % 2 === 0 ? '#f9f9f9' : '#fff');
                                        const events = m.tracking_events || [];
                                        const latestEvent = events.length > 0 ? events[events.length - 1] : null;
                                        const isExpanded = expandedManifests.has(m.id);
                                        const onTimeStatus = getOnTimeStatus(events, m.booking_time_from, m.booking_time_to);

                                        return (
                                            <React.Fragment key={m.id}>
                                                <tr style={{ background: rowBg, color: isDark ? '#ddd' : '#333' }}>
                                                    {/* Expand toggle */}
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center', width: 32 }}>
                                                        {events.length > 0 ? (
                                                            <span
                                                                onClick={() => toggleManifestExpanded(m.id)}
                                                                style={{ cursor: 'pointer', fontSize: '0.9em', opacity: 0.7, userSelect: 'none' }}
                                                                title={isExpanded ? 'Hide tracking events' : `Show ${events.length} tracking event${events.length !== 1 ? 's' : ''}`}
                                                            >
                                                                {isExpanded ? '▼' : '▶'}
                                                            </span>
                                                        ) : (
                                                            <span style={{ opacity: 0.25 }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{m.collection_date ? m.collection_date.split('-').reverse().join('/') : '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{m.delivery_date ? m.delivery_date.split('-').reverse().join('/') : '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{m.consignment_number || '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap', fontFamily: 'monospace', opacity: 0.8 }}>{m.tracking_number || '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd }}>{m.delivery_name || '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{m.delivery_postcode || '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center' }}>{m.number_of_pallets ?? '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{m.weight != null ? `${m.weight} kg` : '—'}</td>
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, opacity: 0.8 }}>{m.customer_reference || '—'}</td>
                                                    {/* Booking window */}
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>
                                                        {m.booking_time_from && m.booking_time_to
                                                            ? `${m.booking_time_from.slice(0, 5)} – ${m.booking_time_to.slice(0, 5)}`
                                                            : '—'}
                                                    </td>
                                                    {/* On time */}
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        {onTimeStatus === null ? (
                                                            <span style={{ opacity: 0.35 }}>—</span>
                                                        ) : onTimeStatus.onTime ? (
                                                            <span
                                                                style={{ fontWeight: 600, color: 'rgba(34,197,94,1)', cursor: 'default' }}
                                                                title={`POD at ${onTimeStatus.podTime} — within booking window ${m.booking_time_from?.slice(0,5)}–${m.booking_time_to?.slice(0,5)}`}
                                                            >✓ On Time</span>
                                                        ) : (
                                                            <span
                                                                style={{ fontWeight: 600, color: 'rgba(220,53,69,0.9)', cursor: 'default' }}
                                                                title={`POD at ${onTimeStatus.podTime} — outside booking window ${m.booking_time_from?.slice(0,5)}–${m.booking_time_to?.slice(0,5)}`}
                                                            >✗ Late</span>
                                                        )}
                                                    </td>
                                                    {/* Latest event */}
                                                    <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>
                                                        {latestEvent ? (
                                                            <span
                                                                style={{ fontWeight: 600, color: eventCodeColor(latestEvent.event_code), cursor: 'default' }}
                                                                title={`${latestEvent.event_code} — ${eventCodeDesc(latestEvent.event_code)}`}
                                                            >
                                                                {eventCodeLabel(latestEvent.event_code)}
                                                                {latestEvent.event_datetime ? (
                                                                    <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 6, fontSize: '0.85em' }}>
                                                                        {new Date(latestEvent.event_datetime).toLocaleDateString('en-GB')}
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                        ) : (
                                                            <span style={{ opacity: 0.4 }}>No events</span>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Expanded tracking events */}
                                                {isExpanded && events.length > 0 && (
                                                    <tr style={{ background: isDark ? '#1a1a1a' : '#f0f4f8' }}>
                                                        <td colSpan={13} style={{ padding: '8px 8px 8px 48px', borderBottom: bd }}>
                                                            {/* Images & Paperwork button */}
                                                            <button
                                                                onClick={() => handleLoadMedia(m.consignment_number)}
                                                                disabled={mediaLoading === m.consignment_number}
                                                                style={{
                                                                    marginBottom: 10, padding: '6px 16px', borderRadius: 6, border: 'none',
                                                                    background: mediaLoading === m.consignment_number ? '#888' : '#6c757d',
                                                                    color: '#fff', cursor: mediaLoading === m.consignment_number ? 'not-allowed' : 'pointer',
                                                                    fontWeight: 600, fontSize: '0.85em',
                                                                }}
                                                            >
                                                                {mediaLoading === m.consignment_number ? 'Loading…' : '🖼 Images & Paperwork'}
                                                            </button>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em', margin: '0 0 8px 0' }}>
                                                                <thead>
                                                                    <tr style={{ opacity: 0.6 }}>
                                                                        <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Date/Time</th>
                                                                        <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Event</th>
                                                                        <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Pallet ID</th>
                                                                        <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Signatory</th>
                                                                        <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {events.map((ev, ei) => (
                                                                        <tr key={ei} style={{ borderTop: `1px solid ${isDark ? '#333' : '#dde'}` }}>
                                                                            <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', opacity: 0.75 }}>
                                                                                {ev.event_datetime ? new Date(ev.event_datetime).toLocaleString('en-GB') : '—'}
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', fontWeight: 600, color: eventCodeColor(ev.event_code) }}
                                                                                title={`${ev.event_code} — ${eventCodeDesc(ev.event_code)}`}
                                                                            >
                                                                                {eventCodeLabel(ev.event_code)}
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px', fontFamily: 'monospace', opacity: 0.8 }}>{ev.pallet_id || '—'}</td>
                                                                            <td style={{ padding: '5px 10px' }}>{ev.signatory || '—'}</td>
                                                                            <td style={{ padding: '5px 10px', opacity: 0.75 }}>{ev.notes || '—'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Report view rendering
    // ─────────────────────────────────────────────────────────────────────────

    const renderReport = () => {
        const inputStyle = {
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#eee' : '#222',
            fontSize: '0.95em',
        };

        return (
            <div style={{ padding: 20 }}>
                {/* Date range picker */}
                <div style={{
                    display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
                    padding: 20, borderRadius: 10,
                    background: isDark ? '#1a1a1a' : '#f5f5f5',
                    border: `1px solid ${isDark ? '#333' : '#ddd'}`,
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>From</label>
                        <input
                            type="date"
                            value={reportStartDate}
                            onChange={(e) => setReportStartDate(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>To</label>
                        <input
                            type="date"
                            value={reportEndDate}
                            onChange={(e) => setReportEndDate(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    {myDeliveries.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={{ fontSize: '0.85em', opacity: 0.7, fontWeight: 600 }}>Delivery Point</label>
                            <select
                                value={selectedDeliveryId}
                                onChange={(e) => setSelectedDeliveryId(e.target.value)}
                                style={{ ...inputStyle, minWidth: 180 }}
                            >
                                <option value="">All Delivery Points</option>
                                {myDeliveries.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.company_name || d.route_name || `#${d.id}`}
                                        {d.route_name && d.company_name ? ` — ${d.route_name}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button
                        onClick={handleFetchReport}
                        disabled={reportLoading}
                        style={{
                            padding: '9px 22px', border: 'none', borderRadius: 6,
                            background: reportLoading ? '#ccc' : '#007bff',
                            color: '#fff', cursor: reportLoading ? 'not-allowed' : 'pointer',
                            fontWeight: 600, fontSize: '0.95em',
                        }}
                    >
                        {reportLoading ? 'Loading…' : 'Get Report'}
                    </button>

                    {reportData && reportData.length > 0 && (
                        <button
                            onClick={() => downloadCSV(reportData)}
                            style={{
                                padding: '9px 22px', border: 'none', borderRadius: 6,
                                background: '#28a745', color: '#fff', cursor: 'pointer',
                                fontWeight: 600, fontSize: '0.95em', display: 'flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} /> Download CSV
                        </button>
                    )}
                </div>

                {/* Report table */}
                {reportData === null ? (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>Select a date range and click "Get Report".</p>
                ) : reportData.length === 0 ? (
                    <p style={{ opacity: 0.6, textAlign: 'center', marginTop: 40 }}>No routes found for this period.</p>
                ) : (
                    <>
                        <p style={{ opacity: 0.7, marginBottom: 12, fontSize: '0.9em' }}>{reportData.length} route{reportData.length !== 1 ? 's' : ''} found</p>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92em' }}>
                                <thead>
                                    <tr style={{ background: isDark ? '#333' : '#e0e0e0', color: isDark ? '#fff' : '#333' }}>
                                        <th style={{ padding: '10px 12px', borderBottom: `2px solid ${isDark ? '#555' : '#bbb'}`, width: 40 }}></th>
                                        {['Date', 'Consignment', 'Route', 'Vehicle Reg', 'Status', 'Booked', 'Arrived', 'Departed', 'Late', 'Late Reason', 'Delay (mins)', 'Delay Reason', 'Penalties'].map((h) => (
                                            <th key={h} style={{ padding: '10px 12px', borderBottom: `2px solid ${isDark ? '#555' : '#bbb'}`, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.map((r, i) => {
                                        const isCompleted = completedRouteUuids[r.route_uuid] === true || r.completed;
                                        const rowBg = isDark ? (i % 2 === 0 ? '#2a2a2a' : '#222') : (i % 2 === 0 ? '#f9f9f9' : '#fff');
                                        const bd = `1px solid ${isDark ? '#444' : '#e0e0e0'}`;
                                        const statusText = isCompleted ? 'Completed' : r.confirmed ? 'Confirmed' : 'Pending';
                                        const statusColor = isCompleted ? 'rgba(34,197,94,1)' : r.confirmed ? 'rgba(26,157,215,1)' : (isDark ? '#aaa' : '#888');
                                        const dd = r.delivery_details || {};
                                        const isLate = dd.late === true;
                                        const isDelayed = dd.delay_length != null && dd.delay_length > 60;

                                        return (
                                            <tr key={r.route_uuid || i} style={{ background: rowBg, color: isDark ? '#ddd' : '#333' }}>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center', width: 40 }}>
                                                    <FontAwesomeIcon
                                                        icon={faEye}
                                                        style={{
                                                            fontSize: '1.1rem',
                                                            width: 18,
                                                            color: isCompleted ? 'rgba(34,197,94,1)' : 'rgba(150,150,150,0.4)',
                                                            cursor: isCompleted ? 'pointer' : 'default',
                                                        }}
                                                        title={isCompleted ? 'View POD / completion details' : 'Not yet completed'}
                                                        onClick={() => isCompleted && handleViewCompletion(r.route_uuid, r)}
                                                    />
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{r.date ? r.date.split('-').reverse().join('/') : '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{r.route_ref || '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd }}>{r.route || '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{r.vehicles?.reg || '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd }}>
                                                    <span style={{ fontWeight: 600, color: statusColor }}>{statusText}</span>
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{dd.booked_time ? dd.booked_time.slice(0, 5) : '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{dd.arrival_time ? dd.arrival_time.slice(0, 5) : '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, whiteSpace: 'nowrap' }}>{dd.departure_time ? dd.departure_time.slice(0, 5) : '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center' }}>
                                                    {dd.late == null ? '—' : (
                                                        <span style={{ fontWeight: 600, color: isLate ? 'rgba(220,53,69,0.95)' : 'rgba(34,197,94,1)' }}>
                                                            {isLate ? 'Yes' : 'No'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd }}>{dd.late_reason || '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd, textAlign: 'center' }}>
                                                    {dd.delay_length == null ? '—' : (
                                                        <span style={{ fontWeight: 600, color: isDelayed ? 'rgba(220,53,69,0.95)' : 'inherit' }}>
                                                            {dd.delay_length}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd }}>{dd.delay_reason || '—'}</td>
                                                <td style={{ padding: '9px 12px', borderBottom: bd }}>{dd.penalties != null ? `£${Number(dd.penalties).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Main render
    // ─────────────────────────────────────────────────────────────────────────

    if (authLoading || !profile) {
        const bg = theme === 'dark' ? '#080808' : '#fff';
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%', background: bg }}><ActivityIndicator size={50} /></div>;
    }

    const containerBg = isDark ? '#080808' : '#fff';

    return (
        <div id={theme} className="container-fluid closed-nav-home-container">
            <Row>
                {/* ── Left Navigation ─────────────────────────────────────── */}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        <li title="Routes" className={`nav-item ${view === 'calendar' ? 'active' : ''}`} style={{ cursor: 'pointer', background: view === 'calendar' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => setView('calendar')}><FontAwesomeIcon icon={faRoute} style={{ width: 16, flexShrink: 0 }} />{showLeftNav && <span>Routes</span>}</li>
                        <li title="Request Route" className={`nav-item ${view === 'requests' ? 'active' : ''}`} style={{ cursor: 'pointer', background: view === 'requests' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => setView('requests')}><FontAwesomeIcon icon={faPlus} style={{ width: 16, flexShrink: 0 }} />{showLeftNav && <span>Request Route</span>}</li>
                        {hasPalletTracking && (<li title="Network Tracking" className={`nav-item ${view === 'tracking' ? 'active' : ''}`} style={{ cursor: 'pointer', background: view === 'tracking' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => setView('tracking')}><FontAwesomeIcon icon={faLocationDot} style={{ width: 16, flexShrink: 0 }} />{showLeftNav && <span>Network Tracking</span>}</li>)}
                        {hasPalletTracking && (<li title="KPIs" className={`nav-item ${view === 'kpis' ? 'active' : ''}`} style={{ cursor: 'pointer', background: view === 'kpis' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => setView('kpis')}><FontAwesomeIcon icon={faChartLine} style={{ width: 16, flexShrink: 0 }} />{showLeftNav && <span>KPIs</span>}</li>)}
                        <li title="Reports" className={`nav-item ${view === 'report' ? 'active' : ''}`} style={{ cursor: 'pointer', background: view === 'report' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => setView('report')}><FontAwesomeIcon icon={faChartBar} style={{ width: 16, flexShrink: 0 }} />{showLeftNav && <span>Reports</span>}</li>
                    </ul>
                </Col>

                {/* ── Main Content ─────────────────────────────────────────── */}
                <Col className="closed-nav-content" style={{ padding: '20px', minHeight: '100vh', background: containerBg }}>

            {/* ── Calendar view ─────────────────────────────────────────────── */}
            {view === 'calendar' && (
                <div className="calendar" id={theme} style={{ minHeight: 'calc(100vh - 40px)' }}>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 80px)' }}>
                            <ActivityIndicator size={50} />
                        </div>
                    ) : (
                        <>
                            {/* Month navigation */}
                            <div className="header">
                                <button onClick={() => { setCurrentDate(subMonths(currentDate, 1)); setSelectedDate(null); }}>&lt;</button>
                                <h2>{format(currentDate, 'MMMM yyyy')}</h2>
                                <button onClick={() => { setCurrentDate(addMonths(currentDate, 1)); setSelectedDate(null); }}>&gt;</button>
                            </div>

                            {/* Day-of-week headers */}
                            <div className="day-names">
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                            </div>

                            {/* Calendar grid */}
                            <AnimatePresence mode="wait" style={{ zIndex: 0, position: 'static' }}>
                                <motion.div
                                    key={currentDate}
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ duration: 0.5 }}
                                    className="days-grid"
                                    style={{ zIndex: 0, position: 'static' }}
                                >
                                    {generateCalendar()}
                                </motion.div>
                            </AnimatePresence>

                            {/* Day view */}
                            <AnimatePresence>{selectedDate && renderDayView()}</AnimatePresence>
                        </>
                    )}
                </div>
            )}

            {/* ── Route request view ────────────────────────────────────────── */}
            {view === 'requests' && <CustomerRouteRequests isDark={isDark} deliveries={myDeliveries} showAlert={showAlert} />}

            {/* ── Tracking view ─────────────────────────────────────────────── */}
            {view === 'tracking' && hasPalletTracking && renderTracking()}
            {renderMediaModal()}

            {/* ── KPIs view ─────────────────────────────────────────────────── */}
            {view === 'kpis' && hasPalletTracking && renderKpis()}

            {/* ── Report view ───────────────────────────────────────────────── */}
            {view === 'report' && renderReport()}

            {/* ── Delivery Notes modal ─────────────────────────────────────── */}
            {showDeliveryNotesModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: isDark ? '#2a2a2a' : '#fff', color: isDark ? '#eee' : '#333', borderRadius: 10, padding: '25px 30px', width: '90%', maxWidth: 700, maxHeight: '80vh', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${isDark ? '#444' : '#eee'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.3em' }}>Delivery Notes — {selectedDate ? new Date(selectedDate).toLocaleDateString() : ''}</h3>
                            <button onClick={() => { setShowDeliveryNotesModal(false); setSelectedDeliveryNoteRoutes([]); }} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: isDark ? '#ccc' : '#666' }}>×</button>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            {completedRoutesForDay.length === 0 ? (
                                <p style={{ textAlign: 'center', opacity: 0.6, marginTop: 20 }}>No completed routes for this date.</p>
                            ) : (
                                <>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'bold', marginBottom: 14, cursor: 'pointer' }}>
                                        <input type="checkbox"
                                            checked={selectedDeliveryNoteRoutes.length === completedRoutesForDay.length}
                                            onChange={(e) => setSelectedDeliveryNoteRoutes(e.target.checked ? completedRoutesForDay.map((r) => r.route_uuid) : [])}
                                        /> Select All ({completedRoutesForDay.length})
                                    </label>
                                    {completedRoutesForDay.map((route) => (
                                        <div key={route.route_uuid} style={{ padding: 14, margin: '8px 0', border: `1px solid ${isDark ? '#555' : '#ddd'}`, borderRadius: 8, background: selectedDeliveryNoteRoutes.includes(route.route_uuid) ? (isDark ? '#2c3e50' : '#f0f8ff') : 'transparent' }}>
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', width: '100%' }}>
                                                <input type="checkbox"
                                                    checked={selectedDeliveryNoteRoutes.includes(route.route_uuid)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setSelectedDeliveryNoteRoutes((p) => [...p, route.route_uuid]);
                                                        else setSelectedDeliveryNoteRoutes((p) => p.filter((id) => id !== route.route_uuid));
                                                    }}
                                                    style={{ marginTop: 2 }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{route.job} — {route.route || 'No route'}</div>
                                                    <div style={{ fontSize: '0.85em', opacity: 0.7 }}>{route.postcode} | {route.route_ref || 'No consignment'}</div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        <div style={{ borderTop: `1px solid ${isDark ? '#444' : '#eee'}`, paddingTop: 14, marginTop: 14, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => { setShowDeliveryNotesModal(false); setSelectedDeliveryNoteRoutes([]); }} disabled={isGeneratingDeliveryNotes} style={{ padding: '9px 20px', border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, background: 'transparent', color: isDark ? '#ccc' : '#666', cursor: 'pointer' }}>Cancel</button>
                            <button
                                onClick={handleGenerateDeliveryNotes}
                                disabled={selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes}
                                style={{ padding: '9px 20px', border: 'none', borderRadius: 6, background: (selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes) ? '#ccc' : '#28a745', color: '#fff', cursor: (selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes) ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                            >
                                {isGeneratingDeliveryNotes ? 'Generating…' : `Download (${selectedDeliveryNoteRoutes.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delivery Details modal (strict read-only: canEdit=false) ─── */}
            {showDeliveryDetailsModal && (
                <DeliveryDetailsModal
                    show={showDeliveryDetailsModal}
                    onClose={handleCloseDeliveryDetails}
                    route={deliveryDetailsRoute}
                    existingRecord={deliveryDetailsRecord}
                    isLoading={deliveryDetailsLoading}
                    theme={theme}
                    canEdit={false}
                />
            )}

            {/* ── Route completion details modal ────────────────────────────── */}
            {showCompletionModal && selectedCompletionData && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowCompletionModal(false); setSelectedCompletionData(null); setSelectedCompletionRoute(null); } }}
                >
                    <div
                        style={{ background: isDark ? '#222' : '#fff', color: isDark ? '#fff' : '#222', borderRadius: 10, border: '2px solid rgba(34,197,94,0.8)', padding: 28, minWidth: 520, maxWidth: 780, maxHeight: '85vh', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}` }}>
                            <h3 style={{ margin: 0, fontSize: '1.3em', color: 'rgba(34,197,94,0.9)' }}>
                                ✓ Delivery Completed — {selectedCompletionData.route_ref || selectedCompletionData.route_uuid}
                            </h3>
                            <button onClick={() => { setShowCompletionModal(false); setSelectedCompletionData(null); setSelectedCompletionRoute(null); }} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: isDark ? '#ccc' : '#444' }}>×</button>
                        </div>

                        {/* Signed by / date */}
                        <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
                            {selectedCompletionData.signed_by && <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Received by</span><br /><strong>{selectedCompletionData.signed_by}</strong></div>}
                            {selectedCompletionData.signed_at && <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Signed at</span><br /><strong>{new Date(selectedCompletionData.signed_at).toLocaleString()}</strong></div>}
                            {selectedCompletionData.submitted_at && <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Submitted</span><br /><strong>{new Date(selectedCompletionData.submitted_at).toLocaleString()}</strong></div>}
                        </div>

                        {/* GPS */}
                        {(selectedCompletionData.latitude || selectedCompletionData.geocoded_address) && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Delivery Location</span><br />
                                {selectedCompletionData.geocoded_address && <div>{selectedCompletionData.geocoded_address}</div>}
                                {selectedCompletionData.latitude && selectedCompletionData.longitude && (
                                    <div
                                        style={{ fontSize: '0.85em', opacity: 0.9, cursor: 'pointer', color: '#007bff', textDecoration: 'underline', padding: '4px 8px', border: '1px solid #007bff20', borderRadius: 4, display: 'inline-block', marginTop: 4 }}
                                        onClick={() => { setSelectedLocation({ lat: parseFloat(selectedCompletionData.latitude), lng: parseFloat(selectedCompletionData.longitude), address: selectedCompletionData.geocoded_address }); setShowLocationModal(true); }}
                                        title="Click to view on map"
                                    >
                                        📍 {selectedCompletionData.latitude}, {selectedCompletionData.longitude}
                                        {selectedCompletionData.location_accuracy ? ` (±${Math.round(selectedCompletionData.location_accuracy)}m)` : ''}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes */}
                        {selectedCompletionData.completion_notes && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Notes</span><br />
                                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedCompletionData.completion_notes}</div>
                            </div>
                        )}

                        {selectedCompletionData.driver_comments && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Driver Comments</span><br />
                                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedCompletionData.driver_comments}</div>
                            </div>
                        )}

                        {/* Signature */}
                        {selectedCompletionData.customer_signature_data && (() => {
                            try {
                                let sig = selectedCompletionData.customer_signature_data;
                                if (typeof sig === 'string' && sig.startsWith('{')) { const d = JSON.parse(sig); sig = d?.signature; }
                                else if (typeof sig !== 'string') { sig = sig?.signature; }
                                return sig ? (
                                    <div style={{ marginBottom: 14 }}>
                                        <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Customer Signature</span>
                                        {selectedCompletionData.signed_by && <div style={{ marginBottom: 6, fontSize: '0.9em', fontWeight: 'bold' }}>{selectedCompletionData.signed_by}</div>}
                                        <img src={sig} alt="Customer signature" style={{ maxWidth: '100%', maxHeight: 160, border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, marginTop: 6, background: '#fff', cursor: 'pointer' }} onClick={() => handleImageClick(sig, `Signature — ${selectedCompletionData.signed_by || ''}`)} title="Click to enlarge" />
                                    </div>
                                ) : null;
                            } catch { return null; }
                        })()}

                        {/* Delivery photos */}
                        {selectedCompletionData.delivery_images?.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Delivery Photos ({selectedCompletionData.delivery_images.length})</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                    {selectedCompletionData.delivery_images.map((url, i) => (
                                        <img key={i} src={url} alt={`Photo ${i + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6, border: `1px solid ${isDark ? '#555' : '#ccc'}`, cursor: 'pointer' }} onClick={() => handleImageClick(url, `Delivery Photo ${i + 1}`)} onError={(e) => { e.target.style.opacity = '0.3'; }} title="Click to enlarge" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Paperwork photos */}
                        {selectedCompletionData.paperwork_images?.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Paperwork Photos ({selectedCompletionData.paperwork_images.length})</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                    {selectedCompletionData.paperwork_images.map((url, i) => (
                                        <img key={i} src={url} alt={`Paperwork ${i + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6, border: `1px solid ${isDark ? '#555' : '#ccc'}`, cursor: 'pointer' }} onClick={() => handleImageClick(url, `Paperwork Photo ${i + 1}`)} onError={(e) => { e.target.style.opacity = '0.3'; }} title="Click to enlarge" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${isDark ? '#444' : '#eee'}`, paddingTop: 14, gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {selectedCompletionData?.delivery_images?.length > 0 && (
                                    <button onClick={() => handleDownloadAll(selectedCompletionData.delivery_images, 'Delivery_Photo')} style={{ padding: '8px 16px', border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, background: 'transparent', color: isDark ? '#ccc' : '#555', cursor: 'pointer', fontSize: '0.9em' }}>
                                        ⬇ Download Photos ({selectedCompletionData.delivery_images.length})
                                    </button>
                                )}
                                {selectedCompletionData?.paperwork_images?.length > 0 && (
                                    <button onClick={() => handleDownloadAll(selectedCompletionData.paperwork_images, 'Paperwork_Photo')} style={{ padding: '8px 16px', border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, background: 'transparent', color: isDark ? '#ccc' : '#555', cursor: 'pointer', fontSize: '0.9em' }}>
                                        ⬇ Download Paperwork ({selectedCompletionData.paperwork_images.length})
                                    </button>
                                )}
                            </div>
                            <button onClick={() => { setShowCompletionModal(false); setSelectedCompletionData(null); setSelectedCompletionRoute(null); }} style={{ padding: '8px 22px', border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, background: 'transparent', color: isDark ? '#ccc' : '#555', cursor: 'pointer' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Image viewer ─────────────────────────────────────────────── */}
            {showImageModal && (
                <div className="modal show" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.8)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) { setShowImageModal(false); setSelectedImageUrl(null); } }}>
                    <div className={`modal-dialog modal-xl modal-dialog-centered`} style={{ margin: 'auto', maxHeight: '90vh', width: '100%' }}>
                        <div className={`modal-content ${isDark ? 'bg-dark text-light' : 'bg-light text-dark'}`}>
                            <div className={`modal-header ${isDark ? 'bg-dark text-light border-secondary' : 'bg-light text-dark border-secondary'}`}>
                                <h5 className="modal-title">📷 {selectedImageTitle}</h5>
                                <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => { setShowImageModal(false); setSelectedImageUrl(null); }} style={{ fontSize: 18, padding: '2px 8px' }}>×</button>
                            </div>
                            <div className={`modal-body text-center ${isDark ? 'bg-dark' : 'bg-light'}`} style={{ padding: 20, maxHeight: 'calc(90vh - 130px)', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                                {isImageLoading ? (
                                    <div className="spinner-border" role="status"><span className="visually-hidden">Loading…</span></div>
                                ) : selectedImageUrl ? (
                                    <img src={selectedImageUrl} alt={selectedImageTitle} style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 200px)', objectFit: 'contain' }} />
                                ) : (
                                    <p>Image unavailable.</p>
                                )}
                            </div>
                            {selectedImageUrl && (
                                <div className={`modal-footer ${isDark ? 'bg-dark border-secondary' : 'bg-light border-secondary'}`}>
                                    <a href={selectedImageUrl} download={`${selectedImageTitle}.jpg`} className="btn btn-primary btn-sm">
                                        <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} /> Download
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Location map modal ───────────────────────────────────────── */}
            {showLocationModal && selectedLocation && (
                <LocationMapModal
                    show={showLocationModal}
                    onHide={() => { setShowLocationModal(false); setSelectedLocation(null); }}
                    location={selectedLocation}
                    title="Delivery Location"
                    zIndex={10000}
                    theme={theme}
                />
            )}
                </Col>
            </Row>
        </div>
    );
};

export default CustomerPortal;
